import { useState } from "react";
import { Activity, Loader2, Lock, ShieldCheck, Zap } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PasswordField } from "@/components/PasswordField";

const Perk = ({ icon: Icon, title, text }) => (
  <div className="flex gap-3">
    <div className="w-8 h-8 shrink-0 grid place-items-center border border-[#163056] bg-[#0B1F3A] rounded-sm"><Icon className="w-4 h-4 text-[#22A34A]" /></div>
    <div><div className="text-sm text-white font-semibold">{title}</div><div className="text-xs text-slate-500 leading-relaxed">{text}</div></div>
  </div>
);

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [fpEmail, setFpEmail] = useState("");
  const [fpMsg, setFpMsg] = useState("");
  const [fpBusy, setFpBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { mode === "login" ? await login(f.email, f.password) : await register(f.email, f.password, f.name); }
    catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setFpMsg(""); setFpBusy(true);
    try { const r = await api.post("/auth/forgot-password", { email: fpEmail }); setFpMsg(r.data.message || "Request sent."); }
    catch (ex) { setFpMsg(errMsg(ex)); }
    setFpBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#071525] grid lg:grid-cols-[1.1fr_1fr]" data-testid="auth-page">
      <div className="hidden lg:flex flex-col justify-between p-14 border-r border-[#163056] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_20%_20%,#22A34A,transparent_45%),radial-gradient(circle_at_80%_80%,#00E676,transparent_40%)]" />
        <div className="flex items-center gap-2 relative">
          <img src="/nexfin.svg" alt="NEXFIN" className="w-9 h-9 rounded-sm" />
          <span className="font-display font-bold text-white text-lg tracking-tight">nexfin<span className="text-[#22A34A]">.trade</span></span>
        </div>
        <div className="relative space-y-8 max-w-md">
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight leading-[1.05]">Your strategy.<br /><span className="text-[#22A34A]">Executed with precision.</span></h1>
          <p className="text-slate-400 text-sm leading-relaxed">Opening-range breakout automation for NSE equity, F&O and MCX. Paper-trade on a replay feed first, then route real orders to your Alice Blue account behind a risk manager and a kill switch.</p>
          <div className="space-y-5">
            <Perk icon={ShieldCheck} title="Backtest before you risk" text="Win rate, drawdown, equity curve and trade log on 2 years of NSE data." />
            <Perk icon={Zap} title="Paper → Live in one click" text="Same engine, same signals. Switch to live execution when you are ready." />
            <Perk icon={Lock} title="Your keys stay yours" text="Broker sessions are stored encrypted and used only for your orders." />
          </div>
        </div>
        <div className="text-[10px] font-mono text-slate-600 tracking-widest relative">NSE · EQUITY & INDEX · ALICEBLUE ANT API</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm panel p-7 rise" data-testid="auth-form">
          <div className="flex gap-1 mb-6 p-1 bg-[#071525] border border-[#163056] rounded-sm">
            {["login", "register"].map((m) => (
              <button type="button" key={m} data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setErr(""); }}
                className={`flex-1 h-8 text-xs font-semibold rounded-sm transition-colors ${mode === m ? "bg-[#102A4A] text-[#22A34A]" : "text-slate-500 hover:text-white"}`}>
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>
          <h2 className="font-display text-xl font-bold text-white mb-1">{mode === "login" ? "Welcome back" : "Start trading smarter"}</h2>
          <p className="text-xs text-slate-500 mb-5">{mode === "login" ? "Sign in to access your strategies and bots." : "Free account includes backtesting and paper trading."}</p>
          <div className="space-y-3">
            {mode === "register" && (
              <label className="flex flex-col gap-1"><span className="widget-title">Full name</span>
                <input data-testid="auth-name-input" className="field h-9" value={f.name} onChange={set("name")} required placeholder="Pavan Kumar" /></label>
            )}
            <label className="flex flex-col gap-1"><span className="widget-title">Email</span>
              <input data-testid="auth-email-input" type="email" className="field h-9" value={f.email} onChange={set("email")} required placeholder="you@example.com" /></label>
            <label className="flex flex-col gap-1"><span className="widget-title">Password</span>
              <PasswordField testid="auth-password-input" value={f.password} onChange={set("password")} required minLength={6} autoComplete={mode === "login" ? "current-password" : "new-password"} /></label>
          </div>
          {mode === "login" && (
            <button type="button" data-testid="forgot-password-link" onClick={() => { setForgot(true); setFpEmail(f.email); setFpMsg(""); }} className="text-[11px] text-[#22A34A] hover:underline mt-2">Forgot password?</button>
          )}
          {err && <p data-testid="auth-error" className="text-xs text-[#FF3B30] mt-3">{err}</p>}
          <button data-testid="auth-submit-button" disabled={busy} className="btn-primary w-full h-10 mt-5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>
      </div>

      {forgot && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" data-testid="forgot-modal" onClick={() => setForgot(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitForgot} className="w-full max-w-sm panel p-6 rise">
            <h3 className="font-display text-lg font-bold text-white mb-1">Reset your password</h3>
            <p className="text-xs text-slate-500 mb-4">Enter your account email. Your administrator will be notified to issue a temporary password you can change after signing in.</p>
            <label className="flex flex-col gap-1"><span className="widget-title">Email</span>
              <input data-testid="forgot-email-input" type="email" className="field h-9" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} required placeholder="you@example.com" /></label>
            {fpMsg && <p data-testid="forgot-message" className="text-xs text-[#22A34A] mt-3 leading-relaxed">{fpMsg}</p>}
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setForgot(false)} className="btn-ghost h-9 flex-1" data-testid="forgot-close">Close</button>
              <button type="submit" disabled={fpBusy} className="btn-primary h-9 flex-1" data-testid="forgot-submit">{fpBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send request"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
